import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Card, CardContent, Grid, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Avatar, Drawer, Button, IconButton, CircularProgress, Chip,
  Divider, InputAdornment, List, ListItem, ListItemText, Dialog,
  DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import { exportToExcel } from '../utils/excel';

const MEDIA_URL = import.meta.env.VITE_MEDIA_URL || '/media/';

interface Student {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  rut: string | null;
  phone: string;
  moodle_id: number | null;
  moodle_username: string;
  full_name: string;
  avatar_url?: string;
}

interface CourseGrade {
  id: number;
  item_name: string;
  item_type: string;
  grade: number | null;
  grade_min: number;
  grade_max: number;
  feedback: string;
  date_graded: string;
}

interface CourseReport {
  course_id: number;
  enrollment_id: number;
  short_name: string;
  full_name: string;
  role: string;
  grades: CourseGrade[];
  activities: any[];
}

export const StudentsPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentReports, setStudentReports] = useState<CourseReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Grade Edit Dialog states
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
  const [editingGrade, setEditingGrade] = useState<CourseGrade | null>(null);
  const [savingGrade, setSavingGrade] = useState(false);
  const [gradeForm, setGradeForm] = useState({
    item_name: '',
    grade: '',
    feedback: ''
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get('/auth/users/?role=student');
      if (response.data && response.data.results) {
        const mapped = response.data.results.map((st: any) => ({
          ...st,
          avatar_url: st.moodle_id ? `${MEDIA_URL}avatars/avatar_moodle_${st.moodle_id}.jpg` : undefined
        }));
        setStudents(mapped);
        setFilteredStudents(mapped);
      }
    } catch (err) {
      console.error('Failed to fetch students:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearch(term);
    
    const filtered = students.filter(st => 
      st.full_name.toLowerCase().includes(term) ||
      st.email.toLowerCase().includes(term) ||
      (st.rut && st.rut.toLowerCase().includes(term))
    );
    setFilteredStudents(filtered);
  };

  const loadPerformanceReports = async (student: Student) => {
    setLoadingReports(true);
    setStudentReports([]);
    try {
      const enrollmentsRes = await apiClient.get(`/moodle/enrollments/?user=${student.id}`);
      const reports: CourseReport[] = [];

      if (enrollmentsRes.data && enrollmentsRes.data.results) {
        for (const enrol of enrollmentsRes.data.results) {
          const course = enrol.course;
          
          // Fetch grades for this enrollment
          const gradesRes = await apiClient.get(`/moodle/grades/?enrollment__user=${student.id}&enrollment__course=${course.id}`);
          
          reports.push({
            course_id: course.id,
            enrollment_id: enrol.id,
            short_name: course.short_name,
            full_name: course.full_name,
            role: enrol.role === 'student' ? 'Estudiante' : 'Docente',
            grades: gradesRes.data?.results || [],
            activities: []
          });
        }
      }
      setStudentReports(reports);
    } catch (err) {
      console.error('Failed to load performance reports:', err);
      setToast({ open: true, message: 'No se pudieron cargar todas las calificaciones.', severity: 'error' });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleStudentClick = async (student: Student) => {
    setSelectedStudent(student);
    setDrawerOpen(true);
    await loadPerformanceReports(student);
  };

  // Open Grade Creation Modal
  const handleOpenCreateGrade = (enrollmentId: number) => {
    setSelectedEnrollmentId(enrollmentId);
    setEditingGrade(null);
    setGradeForm({
      item_name: '',
      grade: '',
      feedback: ''
    });
    setGradeDialogOpen(true);
  };

  // Open Grade Editing Modal
  const handleOpenEditGrade = (enrollmentId: number, grade: CourseGrade) => {
    setSelectedEnrollmentId(enrollmentId);
    setEditingGrade(grade);
    setGradeForm({
      item_name: grade.item_name,
      grade: grade.grade !== null ? String(grade.grade) : '',
      feedback: grade.feedback || ''
    });
    setGradeDialogOpen(true);
  };

  const handleGradeFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGradeForm(prev => ({ ...prev, [name]: value }));
  };

  const handleGradeFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEnrollmentId) return;
    setSavingGrade(true);

    try {
      const payload = {
        enrollment: selectedEnrollmentId,
        item_name: gradeForm.item_name,
        grade: gradeForm.grade !== '' ? parseFloat(gradeForm.grade) : null,
        feedback: gradeForm.feedback,
        item_type: editingGrade ? editingGrade.item_type : 'manual',
        grade_min: 0,
        grade_max: 100
      };

      if (editingGrade) {
        // PATCH existing grade
        await apiClient.patch(`/moodle/grades/${editingGrade.id}/`, {
          grade: payload.grade,
          feedback: payload.feedback
        });
        setToast({ open: true, message: 'Calificación modificada e incorporada a auditoría con éxito.', severity: 'success' });
      } else {
        // POST new manual grade
        await apiClient.post('/moodle/grades/', payload);
        setToast({ open: true, message: 'Calificación manual cargada e incorporada a auditoría con éxito.', severity: 'success' });
      }
      setGradeDialogOpen(false);
      
      // Refresh current report details
      if (selectedStudent) {
        await loadPerformanceReports(selectedStudent);
      }
    } catch (err: any) {
      console.error(err);
      const errMsg = err.response?.data?.detail || 'Error al guardar la calificación.';
      setToast({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setSavingGrade(false);
    }
  };

  const handleExportStudents = () => {
    const headers = ['ID', 'Email', 'Nombre Completo', 'RUT', 'Moodle ID', 'Moodle Username', 'Teléfono'];
    const keys = ['id', 'email', 'full_name', 'rut', 'moodle_id', 'moodle_username', 'phone'];
    exportToExcel(filteredStudents, headers, keys, 'Directorio_Alumnos_CreaSync');
  };

  const handleExportBoletin = (report: CourseReport) => {
    const headers = ['Evaluación', 'Tipo', 'Nota Obtenida', 'Nota Mínima', 'Nota Máxima', 'Retroalimentación', 'Fecha'];
    const keys = ['item_name', 'item_type', 'grade', 'grade_min', 'grade_max', 'feedback', 'date_graded'];
    exportToExcel(report.grades, headers, keys, `Boletin_${selectedStudent?.first_name}_${selectedStudent?.last_name}_${report.short_name}`);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getGradeColor = (grade: number | null) => {
    if (grade === null) return '#CBD5E1';
    if (grade >= 90) return '#00D9A5'; // Excellent
    if (grade >= 70) return '#0070E0'; // Good
    if (grade >= 50) return '#FF9F43'; // Regular
    return '#FF5252'; // Needs improvement
  };

  const getGradeTypeChip = (itemType: string) => {
    const typeLower = itemType ? itemType.toLowerCase() : '';
    
    if (typeLower.includes('tarea') || typeLower.includes('assignment')) {
      return { label: 'Tarea', color: '#0070E0', bgColor: 'rgba(0, 112, 224, 0.06)' };
    }
    if (typeLower.includes('cuestionario') || typeLower.includes('quiz') || typeLower.includes('examen') || typeLower.includes('evaluación')) {
      return { label: 'Cuestionario', color: '#7C4DFF', bgColor: 'rgba(124, 77, 255, 0.06)' };
    }
    if (typeLower.includes('manual')) {
      return { label: 'Manual', color: '#FF9F43', bgColor: 'rgba(255, 159, 67, 0.06)' };
    }
    if (typeLower.includes('cálculo total') || typeLower.includes('total') || typeLower.includes('promedio')) {
      return { label: 'Total', color: '#00D9A5', bgColor: 'rgba(0, 217, 165, 0.06)', fontWeight: 800 };
    }
    
    // Default or general synced item
    const displayLabel = itemType === 'grade_item' ? 'Evaluación' : (itemType || 'Evaluación');
    return { label: displayLabel, color: '#64748B', bgColor: 'rgba(100, 116, 139, 0.06)' };
  };

  const isStaffOrAdmin = user && (user.role === 'admin' || user.role === 'coordinator');

  return (
    <Container maxWidth="lg" className="fade-in-up" sx={{ py: 4, position: 'relative' }}>
      {/* Background glow animation */}
      <Box 
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,112,224,0.04) 0%, rgba(124,77,255,0.01) 75%)',
          filter: 'blur(50px)',
          zIndex: -1,
          animation: 'breathing-nebula 8s ease-in-out infinite'
        }}
      />

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={4} gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton href="/" sx={{ mr: 2, bgcolor: 'background.paper', border: '1px solid #E2E8F0' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#0A2540' }}>
              <SchoolIcon color="primary" sx={{ fontSize: 36 }} />
              Directorio de Estudiantes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Consulta el historial completo de alumnos, avatares y récords sincronizados
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="outlined"
          onClick={handleExportStudents}
          startIcon={<FileDownloadIcon />}
          fullWidth={isMobile}
          sx={{ 
            borderColor: '#00B074', 
            color: '#00B074', 
            borderRadius: 30,
            px: 3,
            '&:hover': {
              borderColor: '#008C5C',
              backgroundColor: 'rgba(0, 176, 116, 0.05)'
            }
          }}
        >
          Exportar Alumnos
        </Button>
      </Box>

      {/* Filter and stats */}
      <Card variant="outlined" sx={{ mb: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, correo electrónico o RUT..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
              <Typography variant="body2" color="text.secondary">
                Alumnos Totales: <Typography component="span" fontWeight={750} color="primary">{students.length}</Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Coincidencias: <Typography component="span" fontWeight={750} color="secondary.main">{filteredStudents.length}</Typography>
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={12}>
          <CircularProgress color="secondary" />
        </Box>
      ) : filteredStudents.length === 0 ? (
        <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron estudiantes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Asegúrate de que la búsqueda sea correcta.
          </Typography>
        </Card>
      ) : isMobile ? (
        // Mobile-native responsive list cards
        <Box display="flex" flexDirection="column" gap={2}>
          {filteredStudents.map(student => (
            <Card 
              key={student.id} 
              variant="outlined" 
              onClick={() => handleStudentClick(student)}
              sx={{ borderRadius: 3, p: 1, cursor: 'pointer', '&:hover': { transform: 'none', boxShadow: 'none' } }}
            >
              <Box display="flex" alignItems="center" gap={2} p={1.5}>
                <Avatar 
                  src={student.avatar_url}
                  alt={student.full_name}
                  sx={{ 
                    width: 46, 
                    height: 46, 
                    border: '2px solid #E2E8F0',
                    backgroundColor: 'rgba(0,112,224,0.04)',
                    color: 'secondary.main',
                    fontWeight: 700
                  }}
                >
                  {getInitials(student.full_name)}
                </Avatar>
                <Box flexGrow={1}>
                  <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                    {student.full_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.2 }}>
                    {student.email}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.8}>
                    {student.moodle_id ? (
                      <Chip label={`Moodle ID: ${student.moodle_id}`} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
                    ) : (
                      <Chip label="Local" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop table
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ backgroundColor: 'rgba(241, 245, 249, 0.6)' }}>
              <TableRow>
                <TableCell width={85} sx={{ fontWeight: 700 }}>Foto</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nombre Alumno</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Correo Electrónico</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>RUT</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Moodle ID</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.map((student) => (
                <TableRow 
                  key={student.id} 
                  hover 
                  onClick={() => handleStudentClick(student)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Avatar 
                      src={student.avatar_url}
                      alt={student.full_name}
                      sx={{ 
                        width: 44, 
                        height: 44, 
                        border: '1.5px solid #E2E8F0',
                        backgroundColor: 'rgba(0,112,224,0.05)',
                        color: '#0070E0',
                        fontWeight: 700,
                        boxShadow: '0 2px 8px rgba(10,37,64,0.03)'
                      }}
                    >
                      {getInitials(student.full_name)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={750} variant="body2" sx={{ color: '#0A2540' }}>{student.full_name}</Typography>
                  </TableCell>
                  <TableCell>{student.email}</TableCell>
                  <TableCell>{student.rut || '-'}</TableCell>
                  <TableCell>
                    {student.moodle_id ? (
                      <Chip label={`ID: ${student.moodle_id}`} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Chip label="Local" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button variant="outlined" size="small" color="secondary" sx={{ borderRadius: 30, px: 2 }}>
                      Ver Historial
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Performance Side Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600 }, p: { xs: 3, sm: 4 }, boxSizing: 'border-box' }
        }}
      >
        {selectedStudent && (
          <Box display="flex" flexDirection="column" height="100%">
            {/* Drawer Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#0A2540' }}>
                Desempeño Académico Histórico
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} sx={{ border: '1px solid #E2E8F0' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Profile Summary Card */}
            <Card variant="outlined" sx={{ mb: 4, p: 2, background: 'rgba(0, 112, 224, 0.01)', borderColor: 'rgba(0, 112, 224, 0.15)', borderRadius: 3 }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  src={selectedStudent.avatar_url}
                  sx={{ width: 64, height: 64, border: '2px solid #0070E0', mr: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                >
                  {getInitials(selectedStudent.full_name)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                    {selectedStudent.full_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedStudent.email}
                  </Typography>
                  {selectedStudent.rut && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.2 }}>
                      RUT: {selectedStudent.rut}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Card>

            {/* Performance Details */}
            <Box flex={1} overflow="auto" sx={{ pr: 0.5 }}>
              {loadingReports ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress color="secondary" />
                </Box>
              ) : studentReports.length === 0 ? (
                <Typography color="text.secondary" align="center" py={4}>
                  No se registran inscripciones a cursos sincronizados para este estudiante.
                </Typography>
              ) : (
                studentReports.map((report) => (
                  <Card key={report.course_id} variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                        <Box>
                          <Typography fontWeight={800} variant="subtitle1" sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                            {report.full_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Código: {report.short_name}
                          </Typography>
                        </Box>
                        <Chip label={report.role} color="secondary" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      {/* Grades Sub-Table */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                        <Typography fontWeight={750} variant="body2" color="secondary" display="flex" alignItems="center" sx={{ gap: 0.5 }}>
                          <LibraryBooksIcon sx={{ fontSize: 16 }} /> Calificaciones Sincronizadas
                        </Typography>
                        <Box display="flex" gap={1}>
                          {isStaffOrAdmin && (
                            <Button 
                              size="small" 
                              variant="text" 
                              color="secondary"
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={() => handleOpenCreateGrade(report.enrollment_id)}
                              sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem', p: 0 }}
                            >
                              Nota Manual
                            </Button>
                          )}
                          <IconButton size="small" onClick={() => handleExportBoletin(report)} color="secondary" sx={{ p: 0.2 }}>
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {report.grades.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2, p: 1 }}>
                          No hay calificaciones reportadas.
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
                          <Table size="small">
                            <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}><Typography fontWeight={700} variant="caption">Evaluación</Typography></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }} width={80}><Typography fontWeight={700} variant="caption">Nota</Typography></TableCell>
                                {isStaffOrAdmin && <TableCell align="center" sx={{ fontWeight: 700 }} width={60}><Typography fontWeight={700} variant="caption">Editar</Typography></TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {report.grades.map((grade) => {
                                const chip = getGradeTypeChip(grade.item_type);
                                return (
                                  <TableRow key={grade.id} hover>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                                        <Typography variant="caption" fontWeight={750} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                                          {grade.item_name}
                                        </Typography>
                                        <Chip 
                                          label={chip.label} 
                                          size="small" 
                                          sx={{ 
                                            height: 18, 
                                            fontSize: '0.62rem', 
                                            fontWeight: chip.fontWeight || 700,
                                            color: chip.color, 
                                            backgroundColor: chip.bgColor,
                                            border: `1px solid ${chip.color}25`,
                                            borderRadius: '6px',
                                            textTransform: 'uppercase'
                                          }} 
                                        />
                                      </Box>
                                      {(() => {
                                        const clean = (grade.feedback || '')
                                          .replace(/&nbsp;/g, ' ')
                                          .replace(/<[^>]*>/g, '')
                                          .trim();
                                        return clean ? (
                                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.2 }}>
                                            Retro: {clean}
                                          </Typography>
                                        ) : null;
                                      })()}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight={850} sx={{ color: getGradeColor(grade.grade) }}>
                                        {grade.grade !== null ? grade.grade.toFixed(1) : '-'}
                                      </Typography>
                                    </TableCell>
                                    {isStaffOrAdmin && (
                                      <TableCell align="center">
                                        <IconButton 
                                          size="small" 
                                          color="primary"
                                          onClick={() => handleOpenEditGrade(report.enrollment_id, grade)}
                                        >
                                          <EditIcon fontSize="inherit" />
                                        </IconButton>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      {/* Activities completions logs */}
                      <Typography fontWeight={750} variant="body2" mb={1} color="primary" display="flex" alignItems="center" sx={{ gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} /> Actividades y Desempeño
                      </Typography>
                      <List disablePadding>
                        <ListItem disablePadding sx={{ py: 0.5, px: 1, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                          <ListItemText 
                            primary={<Typography variant="caption" display="block" fontWeight={700} sx={{ color: '#0A2540' }}>Avance de Actividades Históricas</Typography>}
                            secondary={
                              <Typography variant="caption" display="block" color="text.secondary">
                                Resguardo y bitácora persistente activa
                              </Typography>
                            }
                          />
                          <Chip label="Resguardado en Local" color="success" size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                        </ListItem>
                      </List>

                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Grade Edit Modal Form (Admins / Coordinators only) */}
      <Dialog 
        open={gradeDialogOpen} 
        onClose={() => !savingGrade && setGradeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0A2540' }}>
            {editingGrade ? 'Editar Calificación' : 'Cargar Nota Manual'}
          </Typography>
          <IconButton onClick={() => setGradeDialogOpen(false)} disabled={savingGrade}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleGradeFormSubmit}>
          <DialogContent dividers sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Evaluación / Actividad"
                  name="item_name"
                  value={gradeForm.item_name}
                  onChange={handleGradeFormChange}
                  required
                  disabled={!!editingGrade}
                  placeholder="ej: Tarea Final, Examen Integral"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Calificación / Nota (0 - 100)"
                  name="grade"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={gradeForm.grade}
                  onChange={handleGradeFormChange}
                  required
                  placeholder="ej: 85.5"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Retroalimentación / Observación"
                  name="feedback"
                  multiline
                  rows={3}
                  value={gradeForm.feedback}
                  onChange={handleGradeFormChange}
                  placeholder="Ingresa comentarios sobre el desempeño..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setGradeDialogOpen(false)} color="inherit" disabled={savingGrade}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={savingGrade}
              sx={{ borderRadius: 30, px: 4 }}
            >
              {savingGrade ? <CircularProgress size={20} color="inherit" /> : 'Guardar Calificación'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StudentsPage;
